import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Zap, Leaf, ShoppingBag, BookOpen, Layers, Cpu, Award, Users, GraduationCap } from 'lucide-react';
import Hero from '../components/sections/Hero';
import Services from '../components/sections/Services';
import HelpByProfile from '../components/sections/HelpByProfile';
import EcosystemGraph from '../components/sections/EcosystemGraph';
import Testimonials from '../components/sections/Testimonials';
import CameraDeputatiSection from '../components/sections/CameraDeputatiSection';
import ClubHomeSection from '../components/sections/ClubHomeSection';
import VerifiedReviews from '../components/VerifiedReviews';
import AdvisorFollowScroll from '../components/AdvisorFollowScroll';
import BrandBanner3D from '../components/BrandBanner3D';
import Reveal from '../components/Reveal';
import SEOHead, { ORGANIZATION_LD, LOCAL_BUSINESS_LD } from '../components/SEOHead';
import { AnimatedCounter, SectionHeading } from '../components/shared';
import { FEATURES, STATS, TEAM, BLOG_POSTS, PARTNERS } from '../data/mock';

const FEATURE_ICONS = [Layers, Cpu, Award, Users];

export default function Home() {
  return (
    <div className="bg-background">
      <SEOHead
        title="Digital Tech Solutions | Soluzioni digitali, IA e formazione certificata"
        description="Siti web, campagne pubblicitarie, consulenza IA e AI Act, formazione certificata e community DTS Club. Modica, Ragusa, Sicilia e tutta Italia."
        path="/"
        jsonLd={[ORGANIZATION_LD, LOCAL_BUSINESS_LD]}
      />
      <AdvisorFollowScroll />
      <Hero />

      {/* ========= Brand presentation video — autoplay muted loop ========= */}
      <section className="py-14 md:py-20 bg-background relative overflow-hidden" data-testid="home-video-presentation">
        <div className="absolute inset-0 pointer-events-none opacity-30 grid-bg" />
        <div className="relative max-w-5xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden border border-lime/25 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
              <div
                className="absolute -inset-10 rounded-[3rem] blur-3xl opacity-50 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle at 20% 30%, rgba(198,242,78,0.25), transparent 60%), radial-gradient(circle at 80% 70%, rgba(96,165,250,0.3), transparent 60%)',
                }}
              />
              <video
                src="https://customer-assets-lqy194kg.emergentagent.net/job_tech-academy-hub-12/artifacts/7785caa21baca620_presentazione%20digital.mp4"
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="metadata"
                className="relative w-full h-auto block bg-black"
                data-testid="home-video-presentation-player"
                aria-label="Video presentazione Digital Tech Solutions"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ========= Brand presentation — animated 3D experience ========= */}
      <BrandBanner3D />

      <HelpByProfile />

      {/* ========= Services overview banner — "I NOSTRI SERVIZI" ========= */}
      <section className="py-14 md:py-20 bg-background relative overflow-hidden" data-testid="home-services-banner">
        <div className="absolute inset-0 pointer-events-none opacity-30 grid-bg" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden border border-lime/25 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
              <div
                className="absolute -inset-10 rounded-[3rem] blur-3xl opacity-50 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle at 20% 20%, rgba(96,165,250,0.3), transparent 60%), radial-gradient(circle at 80% 80%, rgba(245,158,11,0.25), transparent 60%)',
                }}
              />
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_tech-academy-hub-12/artifacts/69f0c13a7bd67543_image.png"
                alt="Digital Tech Solutions — I nostri servizi: luce e gas, siti web, marketing, formazione certificata, servizi certificativi, POS, robotica, pubblicità, innovazione, video, opportunità lavorativa"
                loading="lazy"
                decoding="async"
                className="relative w-full h-auto block"
                data-testid="home-services-banner-img"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Category marquee */}
      <div className="py-6 border-y border-white/8 bg-[#0e1838] overflow-hidden">
        <div className="flex gap-8 w-max animate-marquee text-white/40 font-mono-t text-sm">
          {[...Array(2)].map((_, k) => (
            <React.Fragment key={k}>
              {['( AI & Automazione )', '( Blockchain & Tokenizzazione )', '( Formazione & Certificazioni )', '( Sostenibilita & Energia )', '( Business Digitale )'].map((t, i) => (
                <span key={`${k}-${i}`} className="whitespace-nowrap hover:text-lime transition-colors">{t}</span>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Soluzioni digitali & contenuti - replaced by Ecosystem Graph */}
      <EcosystemGraph />

      {/* ========= DTS ROBOTICS teaser ========= */}
      <section className="py-24 md:py-28 bg-[#0a1230] relative overflow-hidden" data-testid="home-robotica">
        <div className="absolute inset-0 opacity-20 grid-bg" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <p className="mono-label mb-3">_dts robotics</p>
            <h2 className="font-display font-bold text-3xl md:text-5xl text-white leading-[1.1]">
              La <span className="gradient-text">robotica intelligente</span> entra nel tuo business.
            </h2>
            <p className="text-white/70 text-lg mt-5 max-w-xl">
              Robot cameriere, pulizia e hospitality progettati per supportare il personale, ottimizzare i processi e offrire ai clienti un'esperienza nuova. Innovazione, efficienza, zero compromessi.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-7 max-w-xl">
              {[
                { label: 'Ristorazione', color: '#8ab4ff' },
                { label: 'Pulizia pro', color: '#c6f24e' },
                { label: 'Hospitality', color: '#f0abfc' },
                { label: 'Logistica', color: '#34d399' },
                { label: 'Hotel & resort', color: '#fbbf24' },
                { label: 'Attivita commerciali', color: '#60a5fa' },
              ].map((b) => (
                <span key={b.label} className="px-3 py-2 rounded-xl text-xs font-mono-t uppercase bg-white/5 border border-white/10 text-white/70" style={{ borderColor: `${b.color}33` }}>
                  {b.label}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/robotica" data-testid="home-robotica-cta-scopri" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-lime text-background font-semibold hover:scale-105 transition-transform">
                Scopri i robot <ArrowUpRight size={16} />
              </Link>
              <Link to="/robotica#robotica-lead" data-testid="home-robotica-cta-consulenza" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white hover:border-lime hover:text-lime transition-colors">
                Richiedi una consulenza gratuita
              </Link>
            </div>
          </div>
          <div className="relative mx-auto max-w-md lg:max-w-none">
            <div
              className="absolute -inset-6 rounded-[2.5rem] blur-3xl opacity-50"
              style={{ background: 'radial-gradient(circle at 30% 30%, rgba(138,180,255,0.4), transparent 60%), radial-gradient(circle at 70% 70%, rgba(198,242,78,0.3), transparent 60%)' }}
            />
            <Link to="/robotica" className="block relative rounded-[1.5rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] bg-black/40 hover:scale-[1.01] transition-transform">
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_tech-academy-hub-12/artifacts/87266db83801904a_image.png"
                alt="Digital Tech Solutions Robotics — robot cameriere, pulizia e hospitality"
                loading="lazy"
                decoding="async"
                data-testid="home-robotica-visual"
                className="w-full h-auto block"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Nuova Era + ACDT */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="mono-label mb-4">_la nuova era digitale</p>
            <h2 className="font-display font-bold text-3xl md:text-5xl leading-[1.08] text-white mb-6">
              Costruiamo <span className="gradient-text">connessioni digitali</span> che semplificano il futuro
            </h2>
            <p className="text-white/60 text-lg leading-relaxed mb-6">
              Digital Tech Solutions supporta aziende, professionisti e consulenti nella trasformazione
              digitale attraverso strumenti concreti, formazione certificata e tecnologie sostenibili.
            </p>
            <div className="flex gap-4">
              <Link to="/chi-siamo" className="px-6 py-3 rounded-full bg-lime text-background font-semibold btn-glow transition-all hover:scale-105">Scopri di piu</Link>
              <Link to="/servizi" className="px-6 py-3 rounded-full border border-white/15 text-white hover:border-lime hover:text-lime transition-colors">I servizi</Link>
            </div>
          </Reveal>

          <div className="space-y-5">
            <Reveal delay={100}>
              <div className="glass rounded-2xl p-7 card-hover">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 rounded-xl bg-lime/15 flex items-center justify-center"><Award size={20} className="text-lime" /></span>
                  <h3 className="font-display font-bold text-xl text-white">Associazione ACDT</h3>
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-4">La rete che forma Consulenti e Consumatori Digitali. Formazione certificata, tesseramento e nuove opportunita professionali.</p>
                <Link to="/acdt" className="inline-flex items-center gap-1 text-lime text-sm font-semibold hover:gap-2 transition-all">Scopri di piu <ArrowUpRight size={15} /></Link>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="glass rounded-2xl p-7 card-hover">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 rounded-xl bg-lime/15 flex items-center justify-center"><BookOpen size={20} className="text-lime" /></span>
                  <h3 className="font-display font-bold text-xl text-white">Corsi e percorsi professionali</h3>
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-4">Per chi vuole lavorare nel digitale, nell AI, nella blockchain e nella sostenibilita.</p>
                <Link to="/academy/catalogo" className="inline-flex items-center gap-1 text-lime text-sm font-semibold hover:gap-2 transition-all">Vai all Academy <ArrowUpRight size={15} /></Link>
              </div>
            </Reveal>
            <Reveal delay={300}>
              <div className="glass rounded-2xl p-7 card-hover border border-lime/20" style={{ background: 'linear-gradient(120deg, rgba(198,242,78,0.06), transparent)' }}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 rounded-xl bg-lime/15 flex items-center justify-center"><GraduationCap size={20} className="text-lime" /></span>
                  <div>
                    <h3 className="font-display font-bold text-xl text-white leading-tight">Abilitazione in sole 20 ore</h3>
                    <span className="font-mono-t text-lime text-[11px]">_corso certificato acdt</span>
                  </div>
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-4">Corso di abilitazione per <span className="text-white font-medium">Consulente e Consumatore Digitale, Tecnologico e di Sostenibilita</span>: diventa una figura professionale riconosciuta in sole 20 ore.</p>
                <Link to="/academy/corso/consulente-digitale-acdt" className="inline-flex items-center gap-1 text-lime text-sm font-semibold hover:gap-2 transition-all">Ottieni l abilitazione <ArrowUpRight size={15} /></Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* B100 banner */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden border border-lime/25 p-10 md:p-16 text-center" style={{ background: 'linear-gradient(160deg, rgba(198,242,78,0.10), rgba(198,242,78,0.02))' }}>
              <div className="absolute inset-0 grid-bg opacity-30" />
              <div className="relative max-w-3xl mx-auto">
                <p className="mono-label mb-5">_formazione che genera reddito</p>
                <h2 className="font-display font-bold text-3xl md:text-5xl text-white leading-[1.08] mb-6">
                  La formazione e i prodotti digitali diventano una <span className="gradient-text">professione certificata</span> e una vera entrata extra
                </h2>
                <p className="text-white/60 text-lg mb-10">Non solo corsi: un percorso concreto per trasformare le tue competenze digitali in una nuova fonte di guadagno, riconosciuta e tutelata dall'Associazione ACDT.</p>
              </div>
              <div className="relative grid md:grid-cols-3 gap-5 max-w-4xl mx-auto mb-10">
                {[
                  { icon: BookOpen, t: 'Formati', d: 'Corsi certificati su AI, digitale e sostenibilita, con il Tutor AI sempre al tuo fianco.' },
                  { icon: Award, t: 'Certificati', d: 'Ottieni la certificazione ACDT e diventa una figura professionale riconosciuta.' },
                  { icon: Zap, t: 'Guadagna', d: 'Offri servizi e prodotti digitali e genera un\'entrata extra come professionista.' },
                ].map((c, i) => (
                  <div key={c.t} className="glass rounded-2xl p-6 text-left">
                    <span className="w-11 h-11 rounded-xl bg-lime/15 flex items-center justify-center mb-4"><c.icon size={20} className="text-lime" /></span>
                    <p className="font-mono-t text-lime text-xs mb-1">0{i + 1}</p>
                    <h3 className="font-display font-bold text-lg text-white mb-2">{c.t}</h3>
                    <p className="text-white/55 text-sm leading-relaxed">{c.d}</p>
                  </div>
                ))}
              </div>
              <div className="relative flex flex-wrap gap-4 justify-center">
                <Link to="/academy/catalogo" className="px-8 py-4 rounded-full bg-lime text-background font-semibold btn-glow transition-all hover:scale-105">Inizia il percorso</Link>
                <Link to="/acdt" className="px-8 py-4 rounded-full border border-white/15 text-white hover:border-lime hover:text-lime transition-colors">Scopri la certificazione ACDT</Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal><SectionHeading label="mission" title="Specialisti in soluzioni digitali" highlight="per la crescita del business" /></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {FEATURES.map((f, i) => {
              const Icon = FEATURE_ICONS[i];
              return (
                <Reveal key={f.title} delay={i * 80}>
                  <div className="glass rounded-2xl p-7 h-full card-hover">
                    <span className="w-12 h-12 rounded-xl bg-lime/15 flex items-center justify-center mb-5"><Icon size={22} className="text-lime" /></span>
                    <h3 className="font-display font-bold text-lg text-white mb-3">{f.title}</h3>
                    <p className="text-white/55 text-sm leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <Services />

      {/* Stats */}
      <section className="py-24 border-y border-white/8 bg-[#0e1838]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 80}>
              <div className="text-center md:text-left">
                <p className="font-display font-bold text-4xl md:text-5xl gradient-text mb-3">
                  <AnimatedCounter value={s.value} prefix={s.prefix || ''} suffix={s.suffix || ''} />
                </p>
                <h3 className="font-semibold text-white mb-2">{s.label}</h3>
                <p className="text-white/50 text-sm">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Sustainability */}
      <section className="py-24" data-advisor-target="chargebull">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden border border-white/10">
              <img src="https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="Sostenibilita" className="absolute inset-0 w-full h-full object-cover opacity-25" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(7,7,9,0.95), rgba(7,7,9,0.5))' }} />
              <div className="relative p-10 md:p-16 max-w-2xl">
                <span className="w-12 h-12 rounded-xl bg-lime/20 flex items-center justify-center mb-6"><Leaf size={24} className="text-lime" /></span>
                <h2 className="font-display font-bold text-3xl md:text-4xl text-white leading-tight mb-5">Innovare non significa consumare, ma <span className="gradient-text">ottimizzare</span></h2>
                <p className="text-white/60 text-lg mb-8">Crediamo in una tecnologia responsabile, capace di migliorare la vita senza sprechi.</p>
                <div className="flex flex-wrap gap-3 mb-8">
                  {['Energia intelligente', 'Bio-sostenibilita digitale', 'Progetti ESG e Green Tech'].map((t) => (
                    <span key={t} className="px-4 py-2 rounded-full glass text-white/80 text-sm flex items-center gap-2"><Zap size={13} className="text-lime" /> {t}</span>
                  ))}
                </div>
                <Link to="/energy-power" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-lime text-background font-semibold btn-glow transition-all hover:scale-105">Scopri la nostra visione <ArrowUpRight size={18} /></Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Team */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal><SectionHeading label="team" title="Uniamo competenze per creare" highlight="soluzioni intelligenti" /></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-14 max-w-3xl">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 70}>
                <div className="group relative rounded-2xl overflow-hidden border border-white/10 card-hover">
                  <img src={m.img} alt={m.name} className="w-full h-80 object-cover object-top transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full glass text-lime text-[10px] font-mono-t">{m.tag}</span>
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white font-display font-bold">{m.name}</p>
                    <p className="text-lime text-xs">{m.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      <VerifiedReviews
        tag="home"
        title="Clienti che consigliano DTS"
        subtitle="4 recensioni verificate da attivita reali in Sicilia e in Italia."
      />

      <ClubHomeSection />

      <CameraDeputatiSection />

      {/* Strategia B100 - moved lower per user request */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden border border-lime/20 p-10 md:p-16" style={{ background: 'linear-gradient(120deg, rgba(198,242,78,0.08), rgba(198,242,78,0.02))' }}>
              <div className="absolute inset-0 grid-bg opacity-30" />
              <div className="relative max-w-3xl">
                <p className="mono-label mb-4">_strategia b100</p>
                <h2 className="font-display font-bold text-3xl md:text-5xl text-white leading-tight mb-5">
                  Aumenta il fatturato e fidelizza i clienti. <span className="gradient-text">Senza fare sconti.</span>
                </h2>
                <p className="text-white/60 text-lg mb-8">Una strategia digitale che riconosce al cliente il 100% della spesa, mentre tu mantieni i margini e fai crescere frequenza e valore nel tempo.</p>
                <div className="flex flex-wrap gap-4 mb-8">
                  {['Il cliente percepisce 100% valore', 'Tu mantieni 100% redditivita', 'Piu ritorni, piu fiducia'].map((p) => (
                    <span key={p} className="px-4 py-2 rounded-full glass text-white/80 text-sm">{p}</span>
                  ))}
                </div>
                <Link to="/b100" data-testid="home-b100-cta" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-lime text-background font-semibold btn-glow transition-all hover:scale-105">Voglio capire come funziona <ArrowUpRight size={18} /></Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Blog */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-14">
            <Reveal><SectionHeading label="blog" title="Novita, trend e" highlight="approfondimenti" /></Reveal>
            <Link to="/blog" className="hidden md:inline-flex items-center gap-2 text-lime font-semibold hover:gap-3 transition-all">Scopri il Blog <ArrowUpRight size={16} /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {BLOG_POSTS.map((p, i) => (
              <Reveal key={p.id} delay={i * 90}>
                <Link to="/blog" className="block group rounded-2xl overflow-hidden glass card-hover h-full">
                  <div className="overflow-hidden h-48">
                    <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-white/40 mb-3">
                      <span>{p.date}</span><span className="w-1 h-1 rounded-full bg-lime" /><span className="text-lime">{p.category}</span>
                    </div>
                    <h3 className="font-display font-bold text-lg text-white leading-snug group-hover:text-lime transition-colors">{p.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Partners marquee */}
      <section className="py-16 border-y border-white/8 bg-[#0e1838] overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-8 mb-8">
          <p className="mono-label text-center">_partners</p>
        </div>
        <div className="flex gap-6 w-max animate-marquee">
          {[...PARTNERS, ...PARTNERS].map((p, i) => (
            <div key={i} className="px-8 py-4 rounded-xl glass text-white/50 font-display font-bold text-lg whitespace-nowrap hover:text-lime transition-colors">{p}</div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-28">
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
          <Reveal>
            <p className="mono-label mb-5">_entra nel network</p>
            <h2 className="font-display font-bold text-4xl md:text-6xl text-white leading-tight mb-7">Entra nel primo <span className="gradient-text">Centro Commerciale Online</span></h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto mb-10">Unisciti all ecosistema Digital Tech Solutions. Tecnologia, formazione e opportunita per far crescere il tuo business.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contatti" className="px-8 py-4 rounded-full bg-lime text-background font-semibold btn-glow transition-all hover:scale-105">Contattaci ora</Link>
              <Link to="/academy/catalogo" className="px-8 py-4 rounded-full border border-white/15 text-white hover:border-lime hover:text-lime transition-colors">Esplora i corsi</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
